/**
 * Cursor-visibility auditor rules (06-timeline.md §13.5).
 *
 * Two non-spoof advisory rules (the spoof-in-effect advisory rides #254):
 *
 * 1. `cursor.visibility.long_browse` — the browse cursor has diverged from the
 *    session cursor for a significant number of ops (threshold: 20) without any
 *    "copy-forward" fact being written. Fires `info`. Likely benign, but the
 *    GM may have forgotten to return to "now" before authoring.
 *
 * 2. `cursor.visibility.map_hidden` — a RevealTarget has mode `'hidden'`. This
 *    is intentional in many campaigns but worth surfacing as an `info` notice
 *    so the GM can confirm the setting is deliberate ("verify this is
 *    intentional given existing reveals").
 *
 * Both rules gate on `ctx.cursor_visibility_ops` (returns `[]` when absent —
 * progressive disclosure, matching the `recurrence_open_cycle` pattern).
 *
 * Messages are i18n keys + params only — no baked English (CLAUDE.md).
 */

import type { RevealTarget } from '@dl/shared';

import type { RawAdvisory } from '../advisory.js';
import type { AuditContext } from '../engine.js';

/** Rule id for the long-browse advisory. */
export const CURSOR_VISIBILITY_LONG_BROWSE_RULE = 'cursor.visibility.long_browse';

/** Rule id for the hidden-mode advisory. */
export const CURSOR_VISIBILITY_MAP_HIDDEN_RULE = 'cursor.visibility.map_hidden';

/**
 * The op-count threshold beyond which a diverged browse cursor without a
 * copy-forward fact is considered noteworthy.
 */
const LONG_BROWSE_OP_THRESHOLD = 20;

const REVEAL_TARGETS: readonly RevealTarget[] = ['character', 'party', 'user'];

export function cursorVisibilityRules(ctx: AuditContext): RawAdvisory[] {
  const binding = ctx.cursor_visibility_ops;
  if (!binding) return [];

  const out: RawAdvisory[] = [];

  // ── Rule 1: long browse without copy-forward ──────────────────────────────
  if (
    binding.has_active_session &&
    binding.browse_diverged_since_ops >= LONG_BROWSE_OP_THRESHOLD
  ) {
    out.push({
      severity: 'info',
      rule: CURSOR_VISIBILITY_LONG_BROWSE_RULE,
      message_key: 'auditor.cursor_visibility.long_browse',
      params: { op_count: binding.browse_diverged_since_ops },
      fact_ids: [],
      entity_ids: [],
    });
  }

  // ── Rule 2: hidden mode set for any RevealTarget ──────────────────────────
  for (const target of REVEAL_TARGETS) {
    const mode = binding.visibility_map[target];
    if (mode === 'hidden') {
      out.push({
        severity: 'info',
        rule: CURSOR_VISIBILITY_MAP_HIDDEN_RULE,
        message_key: 'auditor.cursor_visibility.map_hidden',
        params: { target },
        fact_ids: [],
        entity_ids: [],
      });
    }
  }

  return out;
}
